import { useQuery } from '@apollo/client'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'
import { GQL_LANDING_SALE_PRODUCTS } from '@features/landing-page-builder/model/productQueries'
import type { LandingProductsPayload } from '@features/landing-page-builder/model/resolveProductsQuery'
import { resolveProductsQuery } from '@features/landing-page-builder/model/resolveProductsQuery'
import LandingProductGroupsBlock from '@features/landing-page-builder/ui/blocks/LandingProductGroupsBlock'
import { ProductsGroupCarousel } from '@features/products-carousel'

import { ProductCarousel } from '@widgets/product-carousel'

import type { Product } from '@shared/generated'

import styles from './LandingProductsBlock.module.scss'

/*
  products: a catalog product section.

  Everything except `group` is payload-driven: the payload names a standard
  backend query (payload.query, e.g. popularProducts / saleProducts / products)
  plus how to narrow and page it, and this block issues exactly that request and
  renders the result in the shared product carousel. That means a new product
  section on a landing is a backend config change, not a frontend release.

  `group` is different — it renders product GROUPS rather than a product list:
  a `groups` array becomes a row of collection cards, while a single `id` renders
  that group's products via the same carousel the main page uses.
*/
export default function LandingProductsBlock (props: LandingBlockProps) {
  const { block, payload } = props

  if (block.subtype === 'group') {
    const p = (payload ?? {}) as LandingProductsPayload
    const hasGroupList = Array.isArray((payload as { groups?: unknown } | null)?.groups)
    const groupId = p.id != null ? String(p.id) : ''

    if (!hasGroupList && !groupId) {
      return null
    }

    return (
      <div className={styles.root}>
        {hasGroupList
          ? <LandingProductGroupsBlock {...props} />
          // The carousel titles itself from the group's own label; payload.name
          // is the section name the admin typed and is not forced over it.
          : <ProductsGroupCarousel id={groupId} />}
      </div>
    )
  }

  return <ProductsQuerySection {...props} />
}

function ProductsQuerySection ({ block, payload, shopId }: LandingBlockProps) {
  const p = (payload ?? {}) as LandingProductsPayload
  const resolved = resolveProductsQuery(block.subtype, p, shopId)

  const { data, loading } = useQuery<Record<string, { edges?: Product[] | null } | null>>(
    // A document is required even when skipped; the skip flag keeps it inert.
    resolved?.document ?? GQL_LANDING_SALE_PRODUCTS,
    {
      variables: resolved?.variables,
      skip: !resolved || !shopId,
      fetchPolicy: 'cache-and-network',
    },
  )

  if (!resolved) {
    return null
  }

  const products = (data?.[resolved.root]?.edges ?? []) as Product[]
  const title = p.name || p.title || p.tabLabel || ''

  if (!loading && products.length === 0) {
    return null
  }

  return (
    <div className={styles.root}>
      <ProductCarousel
        title={title}
        products={products}
        link={resolved.link}
        loading={loading && products.length === 0}
        listName={`Landing: ${title || block.subtype || 'products'}`}
        listId={block.id ?? undefined}
      />
    </div>
  )
}
